import EventBus from 'App/services/EventBus';

/*
	Standalone replacement for Tools.VoiceService (WEB-7834).

	A 1:1 port of the Angular services.voice/VoiceService service
	(upsales/common/directives/react-roots/upVoiceRoot.js), which now delegates to this module.

	The delegation is not cosmetic: the module-level state below (ongoingCall / callState /
	callStart / calleeInfo) is written by the Angular tree (upVoiceRoot's controller calls
	callStarted, callEnded and setCallInfo as the softphone progresses) and read by React
	(EditPhonecallCallBlock, EditPhonecallActivityOutcome, upFlashRoot, ui/Modal). A second
	instance would report "no call in progress" forever, with no error and no failing test.

	Every $rootScope.$broadcast is now EventBus.broadcast. The Angular original was inconsistent
	about which scope it used — hangUp reached for the Tools.$rootScope global while every sibling
	used the injected $rootScope — but in Angular those are the same object, so routing both through
	EventBus (a facade over that very $rootScope) unifies them with no behaviour change. Listeners
	registered with Tools.$rootScope.$on still receive all four channels.
*/

type Named = { name?: string; id?: number };

export type CallInfoPayload = {
	contact?: Named | null;
	client?: Named | null;
	/*
		Declared `number` to match EditPhonecallCallBlock's pre-existing CallData/State types, but at
		runtime this is a STRING — upVoice.js builds it as `prop[...].replace(/ /g, '')`. The mismatch
		predates this module and is only ever rendered as text, so it is carried over rather than fixed
		here; correcting it means retyping the component, which belongs in its own change.
	*/
	number?: number | null;
	related?: unknown;
};

export type CallInfo = {
	callState: string;
	callStart: number | null;
	calleeInfo: CallInfoPayload | null;
};

let ongoingCall = false;
// idle, dialing or inCall
let callState = 'idle';
let callStart: number | null = null;
let calleeInfo: CallInfoPayload | null = null;

export const callStarted = () => {
	EventBus.broadcast('callStatusChanged', { ongoingCall: true });
	ongoingCall = true;
};

// Deliberately nulls callStart but NOT calleeInfo — only setCallInfo('idle') clears the callee.
export const callEnded = () => {
	EventBus.broadcast('callStatusChanged', { ongoingCall: false });
	ongoingCall = false;
	callStart = null;
};

export const callInProgress = () => ongoingCall;

export const getCallInfo = (): CallInfo => ({ callState, callStart, calleeInfo });

export const setCallInfo = (
	state?: string | null,
	callStartTime?: number | null,
	onGoingCallInfo?: CallInfoPayload | null
) => {
	callState = state || callState;
	if (callState === 'idle') {
		callStart = null;
		calleeInfo = null;
	} else {
		callStart = callStartTime || callStart;
		calleeInfo = onGoingCallInfo || calleeInfo;
	}
	EventBus.broadcast('callInfoChange', { callState, callStart, calleeInfo });
};

/*
	A no-op while a call is already in progress, as in the Angular original.

	The trailing setCallInfo(null, null, onGoingCallInfo) does NOT record the callee when the state
	is still 'idle' — setCallInfo nulls calleeInfo on the idle branch before it ever looks at the
	argument. That is pre-existing and load-bearing in practice: upVoiceRoot's 'openVoice' listener
	moves the state to 'dialing' and sets the callee itself. Kept verbatim.
*/
export const callClient = (client: Named | null, number: number | null, related?: unknown) => {
	if (!ongoingCall) {
		const onGoingCallInfo = { client, number, related };
		EventBus.broadcast('openVoice', onGoingCallInfo);
		setCallInfo(null, null, onGoingCallInfo);
	}
};

// Same no-op guard; the client is derived off the contact rather than passed in.
export const callContact = (
	contact: (Named & { client?: Named | null }) | null,
	number: number | null,
	related?: unknown
) => {
	if (!ongoingCall) {
		const onGoingCallInfo = { contact, client: contact?.client, number, related };
		EventBus.broadcast('openVoice', onGoingCallInfo);
		setCallInfo(null, null, onGoingCallInfo);
	}
};

export const hangUp = () => {
	EventBus.broadcast('hangUpVoiceReq');
};
